import React, { useEffect, useState } from 'react'
import i18n from 'i18next'
import { useTranslation } from 'react-i18next'
import { isEqual } from 'lodash'
import { makeStyles } from 'tss-react/mui'
import type { Theme } from '@mui/material'
import {
  Button,
  List,
  RadioGroup,
  ListItem,
  FormControlLabel,
  Checkbox,
  Link,
} from '@mui/material'
import Stack from 'ui/layout/Stack'
import { PrescriptionSetting, PrescriptionType } from 'types/med'
import MarginRightCancelButton from 'ui/shorthands/MarginRightCancelButton'
import RadioButtonWithLabel from 'ui/partials/RadioButtonWithLabel'
import { todokusuriClinicUrl } from 'config/url'

export const prescriptionTypeOptions: {
  value: PrescriptionType
  label: string
}[] = [
  {
    value: 'direct',
    label: i18n.t('処方せんを患者へ郵送・直接手渡し（予約時の薬局選択なし）'),
  },
  {
    value: 'pharmacy',
    label: i18n.t('処方せんを患者が指定した薬局へ送付（予約時の薬局選択あり）'),
  },
  { value: 'own', label: i18n.t('自施設から薬剤配送（院内処方・薬局）') },
]

const useStyles = makeStyles()((theme: Theme) => ({
  todokusuri: {
    marginTop: theme.spacing(-1),
    paddingLeft: theme.spacing(5.5),
  },
}))

export type PrescriptionTypeModalContentProps = {
  setting: PrescriptionSetting
  onSubmit: (data: PrescriptionSetting) => Promise<void>
  onClose: () => void
}

const PrescriptionTypeModalContent: React.FC<
  PrescriptionTypeModalContentProps
> = ({ setting, onSubmit, onClose }: PrescriptionTypeModalContentProps) => {
  const { t } = useTranslation()
  const { classes } = useStyles()

  const [prescriptionSetting, setPrescriptionSetting] =
    useState<PrescriptionSetting>(setting)

  const [disabled, setDisabled] = useState<boolean>(true)

  useEffect(() => {
    setDisabled(isEqual(setting, prescriptionSetting))
  }, [setting, prescriptionSetting])

  return (
    <>
      <List>
        <RadioGroup
          onChange={(e) =>
            setPrescriptionSetting((prevState) => {
              const prescriptionType = e.target.value as PrescriptionType
              return {
                prescription_medicine: prescriptionType,
                is_todokusuri:
                  prescriptionType === prevState.prescription_medicine
                    ? prevState.is_todokusuri
                    : null,
              }
            })
          }
          value={prescriptionSetting.prescription_medicine}
        >
          {prescriptionTypeOptions.map(({ value, label }) => (
            <>
              <ListItem component={'div'} key={`${label}${value}`}>
                <RadioButtonWithLabel value={value} label={label} />
              </ListItem>
              {((value === 'direct' &&
                prescriptionSetting.prescription_medicine === 'direct') ||
                (value === 'pharmacy' &&
                  prescriptionSetting.prescription_medicine ===
                    'pharmacy')) && (
                <div className={classes.todokusuri}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={!!prescriptionSetting.is_todokusuri}
                        onChange={(event, checked) =>
                          setPrescriptionSetting({
                            prescription_medicine: value,
                            is_todokusuri: checked,
                          })
                        }
                        color="primary"
                      />
                    }
                    label={
                      <>
                        {t('とどくすりサービスを有効')}（
                        <Link
                          underline={'always'}
                          href={todokusuriClinicUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          {t('とどくすりサービスとは？')}
                        </Link>
                        ）
                      </>
                    }
                  />
                </div>
              )}
            </>
          ))}
        </RadioGroup>
      </List>
      <Stack align={'right'} borderTop>
        <MarginRightCancelButton onClick={onClose} />
        <Button
          color="primary"
          variant="contained"
          type="submit"
          onClick={() => {
            onClose()
            return onSubmit(prescriptionSetting)
          }}
          disabled={disabled}
        >
          {t('保存')}
        </Button>
      </Stack>
    </>
  )
}

export default PrescriptionTypeModalContent
